import { observer } from 'mobx-react-lite';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import CheckIcon from '~/components/MarketplaceIcons/CheckIcon';
import CircleRubleIcon from '~/components/MarketplaceIcons/CircleRubleIcon';
import TruckIcon from '~/components/MarketplaceIcons/TruckIcon';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useNotifyMe } from '~/hooks/useNotifyMe';
import { usePickupEtaRange } from '~/hooks/usePickupEtaRange';
import { usePriceFormatter } from '~/hooks/usePriceFormatter';
import { cartDrawerStore } from '~/pages/DubaiExpress/components/CartDrawerDE/cartDrawerStore';
import type { ProductViewProps } from '~/pages/Product/Product';
import { cartStore } from '~/stores';
import { Offer } from '~/stores/CategoriesStore/Offer';
import { getLowestMultibuyLevel } from '~/utils/multibuy';

import { ProductDEActions } from './ProductDEActions';
import { ProductDEMultibuyLevels } from './ProductDEMultibuyLevels';
import { ProductDEPriceHeadline } from './ProductDEPriceHeadline';

type Props = Pick<
  ProductViewProps,
  | 'isMultiBuy'
  | 'currentPrice'
  | 'currentItem'
  | 'count'
  | 'product'
  | 'limit'
  | 'isBundle'
  | 'handleChangeCount'
  | 'handleFavorite'
> & {
  isLoading?: boolean;
};

interface InfoRow {
  icon: ComponentType<{ className?: string }>;
  text: string;
}

const CTA_CLASS =
  'flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-[#E94E1A] p-0 text-[14px] font-semibold leading-none text-white transition-colors duration-[120ms] [font-family:inherit] hover:bg-[#C43F12]';

/** Marketplace guarantees listed under the CTA, in the order of the mockup. */
const GUARANTEE_KEYS = [
  'productDE:guaranteeEscrow',
  'productDE:guaranteeReturns',
  'productDE:guaranteeCustoms',
] as const;

/**
 * Buy panel of the DubaiExpress product page: price of the selected offer,
 * customs duty and pickup ETA, the add-to-cart control with the favorites
 * and copy-link buttons beside it, and the guarantees. Once the product is
 * in the cart the CTA shows the quantity and opens the cart drawer.
 */
const ProductDEBuyPanelView = ({
  isMultiBuy,
  currentPrice,
  currentItem,
  count,
  product,
  limit,
  isBundle,
  handleChangeCount,
  handleFavorite,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();
  const etaRange = usePickupEtaRange();

  const lowestLevel = currentItem
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;

  const { vibrateOnChange } = useCartVibration({
    isMultibuy: isMultiBuy,
    requiredQuantity: lowestLevel?.quantity ?? 0,
    currentCount: count,
  });

  const {
    notifyMeOnClick,
    notifyMeButtonText,
    isLoading: isSubscriptionLoading,
  } = useNotifyMe(
    product?.data?.id ?? 0,
    product?.selectedOffer?.sku ?? currentItem?.sku ?? '',
    isBundle,
  );

  const customsFee =
    company.showProductCustomsInfo && currentItem instanceof Offer
      ? currentItem.customsFee
      : null;

  const infoRows: InfoRow[] = [];
  if (customsFee) {
    infoRows.push({
      icon: CircleRubleIcon,
      text: `${t('customsDutiesFee')} ${formatPrice(customsFee / 100)}`,
    });
  }
  if (etaRange) {
    infoRows.push({
      icon: TruckIcon,
      text: t('pickupPointEta', { range: etaRange }),
    });
  }

  const handleAdd = () => {
    vibrateOnChange(1, 'add');
    handleChangeCount(1, 'add');
    if (currentItem && cartStore.getCartItemCountById(currentItem) > count) {
      cartDrawerStore.open(currentItem.sku);
    }
  };

  const handleOpenDrawer = () => cartDrawerStore.open(currentItem?.sku);

  const renderCta = () => {
    if (limit <= 0) {
      return (
        <button
          type="button"
          onClick={notifyMeOnClick}
          disabled={isSubscriptionLoading}
          className={cn(
            CTA_CLASS,
            'border border-[#E1E1E1] border-solid bg-white text-[#141415] hover:bg-[#F7F7F7]',
          )}
        >
          {notifyMeButtonText}
        </button>
      );
    }
    const isInCart = count > 0;

    return (
      <button
        type="button"
        onClick={isInCart ? handleOpenDrawer : handleAdd}
        className={CTA_CLASS}
      >
        {isInCart
          ? t('cartDrawerDE:inCartCount', { count })
          : t('addToCartFull')}
      </button>
    );
  };

  return (
    <div className="rounded-[10px] border border-[#EDEDED] border-solid p-3.5">
      <ProductDEPriceHeadline
        isMultiBuy={isMultiBuy}
        currentPrice={currentPrice}
        currentItem={currentItem}
        isLoading={isLoading}
      />
      <ProductDEMultibuyLevels
        isMultiBuy={isMultiBuy}
        currentItem={currentItem}
        count={count}
        isLoading={isLoading}
        className="mt-3"
      />
      {!isLoading && infoRows.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-2.5">
          {infoRows.map(({ icon: RowIcon, text }) => (
            <div
              key={text}
              className="flex items-center gap-1.5 text-[#141415] text-[12px] leading-4"
            >
              <RowIcon className="size-3.5 shrink-0" />
              {text}
            </div>
          ))}
        </div>
      )}
      {isLoading ? (
        <Skeleton height={44} containerClassName="mt-3 block" />
      ) : (
        <div className="mt-3 flex gap-2">
          {renderCta()}
          <ProductDEActions product={product} handleFavorite={handleFavorite} />
        </div>
      )}
      <div className="mt-3 flex flex-col gap-[7px] border-0 border-[#F2F2F2] border-t border-solid pt-3">
        {GUARANTEE_KEYS.map((key) => (
          <div key={key} className="flex items-start gap-2">
            <CheckIcon className="mt-px size-3.5 shrink-0 text-[#E94E1A]" />
            <span className="text-[#5C5C5C] text-[12px] leading-4">
              {t(key)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ProductDEBuyPanel = observer(ProductDEBuyPanelView);
